import Heading from "@/components/Heading";
import { basicSettings } from "@/helper/GridCreator/gridConfig";
import GridCreator from "@/helper/GridCreator/GridCreator";
import { devLog } from "@/helper/helper";
import {
  FinancialAccountsHolding,
  useAccountsHeldElsewhereHoldings,
} from "@/hooks/useAccountsHeldeElsewhereHoldings";
import { FinancialAccount, GridType } from "@/types/types";
import { Tooltip } from "devextreme-react";
import { Position } from "devextreme-react/cjs/tooltip";
import { formatNumber } from "devextreme/localization";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import React, { useCallback, useEffect, useRef, useState } from "react";

type Props<T> = {
  cellData: ColumnCellTemplateData<FinancialAccount>;
  value: T;
  className?: string;
  alreadyFetchedFas: Set<string>;
};

const AHECellRender = <T extends React.ReactNode>({
  cellData,
  value,
  className,
  alreadyFetchedFas,
}: Props<T>) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const faExternalId = cellData?.data?.externalCode;
  const mouseEnterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mouseLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipContentRef = useRef<HTMLDivElement>(null);

  const { data, isSuccess, isLoading, isPending, isRefetching } = useAccountsHeldElsewhereHoldings({
    accountExternalCode: faExternalId!,
    enabled: isHovered || alreadyFetchedFas.has(faExternalId!),
  });

  useEffect(() => {
    return () => {
      if (mouseEnterTimeoutRef.current) clearTimeout(mouseEnterTimeoutRef.current);
      if (mouseLeaveTimeoutRef.current) clearTimeout(mouseLeaveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const hasData = isSuccess && data?.data?.length && !isRefetching;
    const isDataReady = !isLoading && !isPending && !isRefetching && hasData;

    if (
      isHovered &&
      Boolean(faExternalId) &&
      (isLoading || isPending || isRefetching || isDataReady)
    ) {
      setTooltipVisible(true);
    } else {
      setTooltipVisible(false);
    }

    if (isDataReady && tooltipVisible) {
      const renderTimer = setTimeout(() => {
        if (tooltipContentRef.current) {
          tooltipContentRef.current.style.maxHeight = "600px";
          tooltipContentRef.current.style.overflowY = "auto";
        }
      }, 50);

      return () => clearTimeout(renderTimer);
    }
  }, [isLoading, isPending, isRefetching, isSuccess, data, isHovered, faExternalId]);

  const handleMouseEnter = useCallback(() => {
    if (mouseLeaveTimeoutRef.current) {
      clearTimeout(mouseLeaveTimeoutRef.current);
      mouseLeaveTimeoutRef.current = null;
    }

    mouseEnterTimeoutRef.current = setTimeout(() => {
      setIsHovered(true);
      if (!alreadyFetchedFas.has(faExternalId!) && faExternalId) {
        alreadyFetchedFas.add(faExternalId);
        devLog("Fetching data for", faExternalId);
      }
    }, 100);
  }, [faExternalId, alreadyFetchedFas]);

  const handleMouseLeave = useCallback(() => {
    if (mouseEnterTimeoutRef.current) {
      clearTimeout(mouseEnterTimeoutRef.current);
      mouseEnterTimeoutRef.current = null;
    }

    mouseLeaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  }, []);

  const hasData = isSuccess && data?.data?.length && !isRefetching;

  const gridSettings: GridType<FinancialAccountsHolding, string> = {
    ...basicSettings,
    dataSource: data?.data ?? [],
    searchPanel: {
      visible: false,
      width: 240,
    },
    columnChooser: {
      enabled: false,
    },
    filterPanel: {
      visible: false,
    },
    paging: {
      enabled: false,
    },
    pager: {
      visible: false,
    },
    height: "390",
    columns: [
      {
        key: "id",
        dataField: "id",
        visible: false,
        allowEditing: false,
      },
      {
        key: "displayName",
        dataType: "string",
        dataField: "displayName",
        visible: true,
        allowEditing: false,
        allowFiltering: false,
        width: 200,
        caption: "Position",
      },
      {
        key: "amount",
        dataType: "number",
        dataField: "amount",
        visible: true,
        allowEditing: false,
        allowFiltering: false,
        width: 200,
        caption: "Total",
        cellRender: (cellData: ColumnCellTemplateData<FinancialAccountsHolding>) => {
          return (
            <div className="flex h-full items-center justify-center text-sm">
              {Number(cellData?.data?.amount)
                ? formatNumber(Number(cellData?.data?.amount), "$#,##0")
                : "—"}
            </div>
          );
        },
      },
    ],
  };

  return (
    <div
      id={`${faExternalId}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative py-[0.8438rem] text-sm print:px-1 print:py-1 print:!text-[10px] ${className}`}
    >
      {value ?? "—"}
      <Tooltip
        target={`#${faExternalId}`}
        visible={tooltipVisible}
        hideOnOutsideClick={true}
        onShowing={() => {
          if (tooltipContentRef.current) {
            tooltipContentRef.current.scrollTop = 0;
          }
        }}
      >
        <Position at={"right bottom"} my={"bottom"} offset={{ x: 65, y: hasData ? 220 : -10 }} />
        {isLoading || isPending || isRefetching ? <div>Loading...</div> : null}
        <div ref={tooltipContentRef} className="holdings-tooltip max-h-[450px]">
          {hasData ? (
            <>
              <Heading text={`Holdings for ${cellData?.data?.name}`} className="py-2" />
              <GridCreator {...gridSettings} />
            </>
          ) : null}
        </div>
      </Tooltip>
    </div>
  );
};

export default AHECellRender;
